import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTarih, formatTl } from '../lib/api';
import {
  CHANNEL_LABEL, MOVE_REASON_LABEL, UNIT_LABEL,
  type MoveReason, type StockChannel, type Unit,
} from '@artmozaik/shared';

type Detay = {
  id: number; code: string; name: string; unit: Unit; notes: string | null;
  threshold: number; official: number; offrec: number | null;
  listPriceKurus: number | null; maxDiscountPct: number | null;
  category: string; boxM2: number | null; vatPct: number; imageUrl: string | null; active: boolean;
  aliases: { id: number; supplierCode: string; supplier: string | null; nameSnapshot: string | null }[];
  /** null = rol maliyeti göremez; kart hiç çizilmez. */
  priceHistory: { id: number; date: string; unitPriceKurus: number; supplier: string | null; invoiceNo: string | null }[] | null;
  hareketler: { id: number; at: string; channel: StockChannel; qty: number; reason: MoveReason; note: string | null; by: string }[];
};

/**
 * Ürün detayı — yan panel.
 *
 * İki kip var:
 *  · hareket → girdiğin sayı DEĞİŞİM (+/-)
 *  · sayım   → girdiğin sayı YENİ MİKTAR; fark kadar hareket yazılır
 * Sayım kipi, "raftaki gerçek sayı bu" demenin doğru yolu: geçmiş hareketler
 * silinmez, aradaki fark ayrı bir kayıt olarak durur.
 */
export default function StokDetay({
  id, kapat, degisti, yazabilir, faturasizYazabilir, kodDegistirebilir,
}: {
  id: number;
  kapat: () => void;
  degisti: () => void;
  yazabilir: boolean;
  faturasizYazabilir: boolean;
  /** Kod değiştirme yalnız ADMIN'de — şartname 4.2. */
  kodDegistirebilir: boolean;
}) {
  const [d, setD] = useState<Detay | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const [kip, setKip] = useState<'hareket' | 'sayim'>('sayim');
  const [kanal, setKanal] = useState<StockChannel>('OFFICIAL');
  const [miktar, setMiktar] = useState('');
  const [not, setNot] = useState('');
  const [esik, setEsik] = useState('');
  const [bekliyor, setBekliyor] = useState(false);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [kodAcik, setKodAcik] = useState(false);
  /* Ürün bilgisi düzenleme: açılınca alanlar karttaki değerlerle dolar. */
  const [duzen, setDuzen] = useState<null | { name: string; category: string; unit: string; boxM2: string; listPriceTl: string;
    maxDiscountPct: string; vatPct: string; imageUrl: string; notes: string }>(null);
  async function bilgiKaydet(e: React.FormEvent) {
    e.preventDefault();
    if (!duzen) return;
    setHata(null); setBekliyor(true);
    const sayi = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')));
    try {
      await api(`/api/urunler/${id}`, { method: 'PATCH', body: JSON.stringify({
        name: duzen.name, category: duzen.category, unit: duzen.unit, boxM2: sayi(duzen.boxM2),
        listPriceTl: sayi(duzen.listPriceTl), maxDiscountPct: sayi(duzen.maxDiscountPct),
        vatPct: sayi(duzen.vatPct) ?? 20, imageUrl: duzen.imageUrl.trim() || null, notes: duzen.notes.trim() || null,
      }) });
      setBilgi('Ürün bilgisi kaydedildi'); setDuzen(null);
      await yukle(); degisti();
    } catch (err) { setHata(err instanceof Error ? err.message : 'Ürün bilgisi kaydedilemedi'); }
    finally { setBekliyor(false); }
  }
  async function aktiflikDegis(aktif: boolean) {
    if (!aktif && !window.confirm('Ürün pasife alınsın mı? Listelerde ve teklif aramasında görünmez; geçmişi silinmez.')) return;
    setHata(null);
    try {
      await api(`/api/urunler/${id}`, { method: 'PATCH', body: JSON.stringify({ active: aktif }) });
      setBilgi(aktif ? 'Ürün yeniden aktif' : 'Ürün pasife alındı'); await yukle(); degisti();
    } catch (err) { setHata(err instanceof Error ? err.message : 'Kaydedilemedi'); }
  }
  const [yeniKod, setYeniKod] = useState('');
  const [kodNot, setKodNot] = useState('');

  const yukle = () =>
    api<Detay>(`/api/urunler/${id}`)
      .then((x) => { setD(x); setEsik(String(x.threshold)); })
      .catch((e) => setHata((e as Error).message));

  useEffect(() => { void yukle(); }, [id]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && kapat();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [kapat]);

  async function hareketGonder(e: React.FormEvent) {
    e.preventDefault();
    setHata(null); setBilgi(null); setBekliyor(true);
    try {
      const r = await api<{ oncesi: number; sonrasi: number }>(`/api/urunler/${id}/hareket`, {
        method: 'POST',
        body: JSON.stringify({ channel: kanal, kip, qty: Number(miktar), note: not }),
      });
      setBilgi(`${CHANNEL_LABEL[kanal]}: ${r.oncesi} → ${r.sonrasi}`);
      setMiktar(''); setNot('');
      await yukle();
      degisti();
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Kaydedilemedi');
    } finally {
      setBekliyor(false);
    }
  }

  async function esikKaydet() {
    setHata(null);
    try {
      await api(`/api/urunler/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ threshold: Number(esik) }),
      });
      setBilgi(`Eşik ${esik} olarak kaydedildi`);
      await yukle();
      degisti();
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Eşik kaydedilemedi');
    }
  }

  async function kodDegistir(e: React.FormEvent) {
    e.preventDefault();
    setHata(null);
    try {
      const r = await api<{ eskiKod: string; yeniKod: string }>(`/api/urunler/${id}/kod`, {
        method: 'POST',
        body: JSON.stringify({ code: yeniKod, note: kodNot }),
      });
      setBilgi(`Kod ${r.eskiKod} → ${r.yeniKod}. Eski kod takma ad olarak kaldı.`);
      setKodAcik(false); setYeniKod(''); setKodNot('');
      await yukle();
      degisti();
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Kod değiştirilemedi');
    }
  }

  return (
    <div className="ccd-drawer-arka" onClick={kapat} role="presentation">
      <aside
        className="ccd-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={d ? `${d.code} detayı` : 'Ürün detayı'}
      >
        {!d ? (
          <div className="ccd-inline-note">Yükleniyor…</div>
        ) : (
          <div className="ccd-stack">
            <div className="ccd-row-between">
              <div>
                <div className="ccd-eyebrow ccd-mono">{d.code}</div>
                <h2 className="ccd-heading">{d.name}</h2>
                <div className="ccd-meta">{d.notes}</div>
                <Link className="ccd-link ccd-stokdetay-bag" to={`/stok/${d.id}`} onClick={kapat}>Ürün kartını aç →</Link>
              </div>
              <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Kapat</button>
            </div>

            {/* Haneler */}
            <div className="ccd-grid ccd-grid-3">
              <div className="ccd-stat">
                <div className="ccd-stat-label">Resmî</div>
                <div className="ccd-stat-value">{d.official}</div>
                <div className="ccd-stat-foot">{UNIT_LABEL[d.unit]}</div>
              </div>
              {d.offrec != null && (
                <div className="ccd-stat">
                  <div className="ccd-stat-label">Faturasız</div>
                  <div className="ccd-stat-value">{d.offrec}</div>
                  <div className="ccd-stat-foot">{UNIT_LABEL[d.unit]}</div>
                </div>
              )}
              <div className="ccd-stat">
                <div className="ccd-stat-label">Liste fiyatı</div>
                <div className="ccd-stat-value" style={{ fontSize: 24 }}>
                  {d.listPriceKurus ? formatTl(d.listPriceKurus) : '—'}
                </div>
                <div className="ccd-stat-foot">
                  {d.maxDiscountPct != null ? `iskonto tavanı %${d.maxDiscountPct}` : 'iskonto tavanı yok'}
                </div>
              </div>
            </div>

            {bilgi && (
              <div className="ccd-banner">
                <span className="ccd-banner-icon" aria-hidden="true">✓</span>
                <span>{bilgi}</span>
              </div>
            )}
            {hata && (
              <div className="ccd-banner ccd-banner-danger">
                <span className="ccd-banner-icon" aria-hidden="true">!</span>
                <span>{hata}</span>
              </div>
            )}

            {/* Ürün bilgisi */}
            {yazabilir && (
              <div className="ccd-card ccd-stack ccd-sd-form">
                <div className="ccd-row-between">
                  <div className="ccd-fieldset-title" style={{ margin: 0 }}>
                    Ürün bilgisi{!d.active && <span className="ccd-badge" style={{ marginLeft: 8 }}>pasif</span>}
                  </div>
                  {!duzen && (
                    <div className="ccd-row" style={{ gap: 8 }}>
                      <button className="ccd-btn ccd-btn-sm" onClick={() => setDuzen({
                        name: d.name, category: d.category, unit: d.unit, boxM2: d.boxM2 == null ? '' : String(d.boxM2),
                        listPriceTl: d.listPriceKurus == null ? '' : String(d.listPriceKurus / 100),
                        maxDiscountPct: d.maxDiscountPct == null ? '' : String(d.maxDiscountPct),
                        vatPct: String(d.vatPct), imageUrl: d.imageUrl ?? '', notes: d.notes ?? '',
                      })}>Bilgileri düzenle</button>
                      <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => aktiflikDegis(!d.active)}>
                        {d.active ? 'Pasife al' : 'Aktifleştir'}
                      </button>
                    </div>
                  )}
                </div>
                {!duzen ? (
                  <div className="ccd-meta">
                    {UNIT_LABEL[d.unit]} · KDV %{d.vatPct}{d.boxM2 != null ? ` · 1 kutu = ${d.boxM2} m²` : ' · kutu m² girilmemiş'}
                    {d.imageUrl ? ' · görsel var' : ' · görsel yok'}
                  </div>
                ) : (
                  <form className="ccd-stack" onSubmit={bilgiKaydet}>
                    <div className="ccd-field">
                      <label className="ccd-field-label" htmlFor="dad">Ürün adı</label>
                      <input id="dad" className="ccd-input" required value={duzen.name} onChange={(e) => setDuzen({ ...duzen, name: e.target.value })} />
                    </div>
                    <div className="ccd-form-grid">
                      <div className="ccd-field">
                        <label className="ccd-field-label" htmlFor="dkat">Kategori</label>
                        <select id="dkat" className="ccd-select" value={duzen.category} onChange={(e) => setDuzen({ ...duzen, category: e.target.value })}>
                          <option value="URETIM">Üretim</option><option value="ALSAT">Al-sat</option>
                          <option value="HAMMADDE">Hammadde</option><option value="HIZMET">Hizmet (stok tutulmaz)</option>
                        </select>
                      </div>
                      <div className="ccd-field">
                        <label className="ccd-field-label" htmlFor="dbirim">Birim</label>
                        <select id="dbirim" className="ccd-select" value={duzen.unit} onChange={(e) => setDuzen({ ...duzen, unit: e.target.value })}>
                          {(['KUTU', 'M2', 'ADET', 'KG', 'LT', 'MTUL'] as Unit[]).map((u) => <option key={u} value={u}>{UNIT_LABEL[u]}</option>)}
                        </select>
                      </div>
                      <div className="ccd-field">
                        <label className="ccd-field-label" htmlFor="dliste">Liste fiyatı (TL)</label>
                        <input id="dliste" className="ccd-input" inputMode="decimal" value={duzen.listPriceTl} onChange={(e) => setDuzen({ ...duzen, listPriceTl: e.target.value })} />
                      </div>
                      <div className="ccd-field">
                        <label className="ccd-field-label" htmlFor="dtavan">İskonto tavanı %</label>
                        <input id="dtavan" className="ccd-input" inputMode="decimal" placeholder="boş = markadan türet" value={duzen.maxDiscountPct} onChange={(e) => setDuzen({ ...duzen, maxDiscountPct: e.target.value })} />
                      </div>
                      <div className="ccd-field">
                        <label className="ccd-field-label" htmlFor="dkdv">KDV %</label>
                        <input id="dkdv" className="ccd-input" inputMode="decimal" value={duzen.vatPct} onChange={(e) => setDuzen({ ...duzen, vatPct: e.target.value })} />
                      </div>
                      <div className="ccd-field">
                        <label className="ccd-field-label" htmlFor="dm2">1 kutu kaç m²</label>
                        <input id="dm2" className="ccd-input" inputMode="decimal" placeholder="örn. 1,44" value={duzen.boxM2} onChange={(e) => setDuzen({ ...duzen, boxM2: e.target.value })} />
                      </div>
                    </div>
                    <div className="ccd-field">
                      <label className="ccd-field-label" htmlFor="dnot">Seri / ebat / yüzey</label>
                      <input id="dnot" className="ccd-input" value={duzen.notes} onChange={(e) => setDuzen({ ...duzen, notes: e.target.value })} />
                    </div>
                    <div className="ccd-field">
                      <label className="ccd-field-label" htmlFor="dgorsel">Görsel adresi</label>
                      <input id="dgorsel" className="ccd-input" placeholder="https://…" value={duzen.imageUrl} onChange={(e) => setDuzen({ ...duzen, imageUrl: e.target.value.trim() })} />
                      {duzen.imageUrl && <img src={duzen.imageUrl} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, marginTop: 8, border: '1px solid var(--ccd-border)' }} />}
                    </div>
                    <div className="ccd-row" style={{ gap: 8 }}>
                      <button className="ccd-btn ccd-btn-primary" disabled={bekliyor}>{bekliyor ? 'Kaydediliyor…' : 'Kaydet'}</button>
                      <button type="button" className="ccd-btn ccd-btn-ghost" onClick={() => setDuzen(null)}>Vazgeç</button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Hareket / sayım */}
            {yazabilir && (
              <form className="ccd-card ccd-stack ccd-sd-form" onSubmit={hareketGonder}>
                <div className="ccd-fieldset-title">Stok düzelt</div>

                <div className="ccd-tabs" role="tablist">
                  <button type="button" role="tab" aria-selected={kip === 'sayim'}
                          className="ccd-tab" onClick={() => setKip('sayim')}>
                    Sayım (yeni miktar)
                  </button>
                  <button type="button" role="tab" aria-selected={kip === 'hareket'}
                          className="ccd-tab" onClick={() => setKip('hareket')}>
                    Hareket (+/−)
                  </button>
                </div>

                <div className="ccd-form-grid">
                  <div className="ccd-field">
                    <label className="ccd-field-label" htmlFor="kanal">Hane</label>
                    <select id="kanal" className="ccd-select" value={kanal}
                            onChange={(e) => setKanal(e.target.value as StockChannel)}>
                      <option value="OFFICIAL">Resmî</option>
                      {faturasizYazabilir && <option value="OFFREC">Faturasız</option>}
                    </select>
                  </div>
                  <div className="ccd-field">
                    <label className="ccd-field-label" htmlFor="mik">
                      {kip === 'sayim' ? 'Raftaki gerçek miktar' : 'Değişim (+/−)'}
                      <span className="ccd-required"> *</span>
                    </label>
                    <input id="mik" className="ccd-input" type="number" inputMode="decimal" step="any" required
                           value={miktar} onChange={(e) => setMiktar(e.target.value)} />
                  </div>
                </div>

                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="not">
                    Not<span className="ccd-required"> *</span>
                  </label>
                  <input id="not" className="ccd-input" required minLength={3}
                         placeholder="Neden değişti? (kırık, sayım farkı, elden çıkış…)"
                         value={not} onChange={(e) => setNot(e.target.value)} />
                  <div className="ccd-field-hint">
                    Not zorunlu: altı ay sonra "bu 12 kutu nereye gitti" sorusunun cevabı bu.
                  </div>
                </div>

                <button className="ccd-btn ccd-btn-primary" disabled={bekliyor}>
                  {bekliyor ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              </form>
            )}

            {/* Eşik */}
            {yazabilir && (
              <div className="ccd-card ccd-row ccd-sd-form" style={{ gap: 12, flexWrap: 'wrap' }}>
                <div className="ccd-field" style={{ flex: '0 0 120px' }}>
                  <label className="ccd-field-label" htmlFor="esik">Uyarı eşiği</label>
                  <input id="esik" className="ccd-input" type="number" inputMode="decimal" step="any"
                         value={esik} onChange={(e) => setEsik(e.target.value)} />
                </div>
                <button className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                        style={{ alignSelf: 'end' }} onClick={esikKaydet}>
                  Eşiği kaydet
                </button>
                <div className="ccd-meta" style={{ flex: '1 1 200px' }}>
                  Toplam bu sayının altına düşünce uyarı üretilir.
                </div>
              </div>
            )}

            {/* Kod değiştirme — yalnız ADMIN */}
            {kodDegistirebilir && (
              <div className="ccd-card ccd-stack ccd-sd-form">
                <div className="ccd-row-between">
                  <div className="ccd-fieldset-title" style={{ margin: 0 }}>ART kodu</div>
                  {!kodAcik && (
                    <button className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                            onClick={() => { setKodAcik(true); setYeniKod(d.code); }}>
                      Kodu değiştir
                    </button>
                  )}
                </div>
                {!kodAcik ? (
                  <div className="ccd-meta">
                    <span className="ccd-mono">{d.code}</span> — kod sabittir. Değiştirilirse
                    eski kod otomatik takma ada düşer, geçmiş kopmaz.
                  </div>
                ) : (
                  <form className="ccd-stack" onSubmit={kodDegistir}>
                    <div className="ccd-form-grid">
                      <div className="ccd-field">
                        <label className="ccd-field-label" htmlFor="yenikod">Yeni kod</label>
                        <input id="yenikod" className="ccd-input ccd-mono" required
                               value={yeniKod} onChange={(e) => setYeniKod(e.target.value.toUpperCase())} />
                      </div>
                      <div className="ccd-field">
                        <label className="ccd-field-label" htmlFor="kodnot">
                          Gerekçe<span className="ccd-required"> *</span>
                        </label>
                        <input id="kodnot" className="ccd-input" required minLength={3}
                               placeholder="Neden değişiyor?"
                               value={kodNot} onChange={(e) => setKodNot(e.target.value)} />
                      </div>
                    </div>
                    <div className="ccd-row">
                      <button className="ccd-btn ccd-btn-primary ccd-btn-sm">Kodu değiştir</button>
                      <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                              onClick={() => setKodAcik(false)}>Vazgeç</button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Tedarikçi kod eşlemeleri */}
            <div className="ccd-card ccd-sd-form">
              <div className="ccd-fieldset-title">Tedarikçi kodları</div>
              {d.aliases.length === 0 ? (
                <div className="ccd-meta">
                  Henüz eşleme yok. İlk alış faturası bağlandığında buraya düşer.
                </div>
              ) : (
                <ul className="ccd-stack-sm" style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                  {d.aliases.map((a) => (
                    <li key={a.id} className="ccd-row">
                      <span className="ccd-mono">{a.supplierCode}</span>
                      <span className="ccd-meta">
                        {a.supplier ?? 'tedarikçi belirsiz'}
                        {a.nameSnapshot ? ` · "${a.nameSnapshot}"` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Hareket geçmişi */}
            <div className="ccd-card ccd-sd-gecmis">
              <div className="ccd-fieldset-title">Hareket geçmişi</div>
              {d.hareketler.length === 0 ? (
                <div className="ccd-meta">Hareket yok.</div>
              ) : (
                <div className="ccd-timeline">
                  {d.hareketler.map((m) => (
                    <div className="ccd-tl-item" key={m.id}>
                      <span className="ccd-tl-dot" />
                      <div className="ccd-tl-body">
                        <div className="ccd-tl-head">
                          <span className="ccd-tl-text">
                            <span className={m.qty < 0 ? 'ccd-kpi-down' : 'ccd-kpi-up'}>
                              {m.qty > 0 ? '+' : ''}{m.qty}
                            </span>{' '}
                            · {CHANNEL_LABEL[m.channel]} · {MOVE_REASON_LABEL[m.reason]}
                          </span>
                          <span className="ccd-tl-time">{formatTarih(m.at)}</span>
                        </div>
                        {m.note && <div className="ccd-meta">{m.note}</div>}
                        <div className="ccd-meta ccd-tl-actor">{m.by}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Alış fiyatı geçmişi */}
            <div className="ccd-card ccd-sd-form" style={d.priceHistory ? undefined : { display: 'none' }}>
              <div className="ccd-fieldset-title">Alış fiyatı geçmişi</div>
              {(d.priceHistory ?? []).length === 0 ? (
                <div className="ccd-meta">
                  Henüz alış faturası bağlanmadı. Bir alış satırı bu ürüne bağlanınca buraya düşer.
                </div>
              ) : (
                <div className="ccd-table-wrap">
                  <table className="ccd-table">
                    <thead>
                      <tr>
                        <th className="ccd-th">Tarih</th>
                        <th className="ccd-th">Tedarikçi</th>
                        <th className="ccd-th">Fatura</th>
                        <th className="ccd-th ccd-td-num">Birim fiyat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(d.priceHistory ?? []).map((h) => (
                        <tr className="ccd-tr" key={h.id}>
                          <td className="ccd-td">{formatTarih(h.date)}</td>
                          <td className="ccd-td">{h.supplier ?? '—'}</td>
                          <td className="ccd-td ccd-mono">{h.invoiceNo ?? '—'}</td>
                          <td className="ccd-td ccd-td-num">{formatTl(h.unitPriceKurus)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {/* Telefon: özet sayfasının altında yapışkan "Karta git" (masaüstünde başlıktaki bağlantı yeterli). */}
            <div className="ccd-cekmece-alt ccd-yalniz-telefon">
              <button type="button" className="ccd-btn" onClick={kapat}>Kapat</button>
              <Link className="ccd-btn ccd-btn-primary" to={`/stok/${d.id}`}>Karta git</Link>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
